import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Content Container */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/20 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3">
            Privacy <span className="text-blue-600">Policy</span>
          </h1>
          <p className="text-slate-500">Last Updated: June 19, 2026</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-xl">
          <div className="prose prose-slate max-w-none">

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
              <p className="text-slate-600 leading-relaxed">
                EXITIUM Labs ("we," "our," or "us") operates a customer communications platform including feedback collection, SMS/MMS messaging, and an AI voice phone agent. This Privacy Policy explains how we collect, use, disclose, and safeguard your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Our Role (Controller vs. Processor)</h2>
              <p className="text-slate-600 leading-relaxed">
                When we provide services to a business ("Business"), the Business decides why and how your personal information is used and acts as the controller (or "business" under California law). In that context, EXITIUM acts as a processor (or "service provider") and handles your information on the Business's behalf and instructions. Requests about information collected for a specific Business may be directed to that Business. We act as a controller for our own platform and operational data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information We Collect</h2>
              <div className="text-slate-600 leading-relaxed space-y-3">
                <p><strong className="text-slate-900">Personal Information:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Phone number</li>
                  <li>Business name (where you provided consent)</li>
                  <li>SMS message content (ratings and feedback)</li>
                  <li>Call audio, transcripts, and related metadata (AI phone agent)</li>
                  <li>Opt-in/opt-out status and timestamps</li>
                </ul>

                <p className="mt-4"><strong className="text-slate-900">Automatically Collected Information:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Message and call delivery status</li>
                  <li>Device information (carrier, phone type)</li>
                  <li>Timestamp of interactions</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. How We Use Your Information</h2>
              <div className="text-slate-600 leading-relaxed">
                <p className="mb-3">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Send customer satisfaction surveys via SMS</li>
                  <li>Deliver service updates and notifications from Businesses you've opted in to</li>
                  <li>Operate the AI voice phone agent and handle calls</li>
                  <li>Process and analyze customer feedback</li>
                  <li>Comply with legal obligations</li>
                  <li>Improve our services and platform functionality (using de-identified or aggregated data)</li>
                  <li>Process opt-out requests (STOP commands)</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. AI &amp; Automated Processing</h2>
              <div className="text-slate-600 leading-relaxed space-y-3">
                <p>We use AI and automated processing in parts of our platform, including invoice scanning, the AI voice phone agent, and content generation. AI outputs may contain errors and are not a substitute for human review. When you interact with the AI phone agent, you are notified at the start of the call that it is an automated system and, where applicable, that the call may be recorded.</p>
                <p>For full details, see our AI &amp; Automated Processing Disclosure.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Information Sharing &amp; Service Providers</h2>
              <div className="text-slate-600 leading-relaxed">
                <p className="mb-3">We share your information with:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-slate-900">Business Clients:</strong> The Business you opted in with receives your phone number and feedback responses</li>
                  <li><strong className="text-slate-900">Service Providers (Subprocessors):</strong> We use third parties to deliver the service, including SMS/MMS delivery, telephony, speech-to-text, text-to-speech, document/AI processing, hosting, and payments</li>
                  <li><strong className="text-slate-900">Legal Requirements:</strong> When required by law or to protect our rights</li>
                </ul>
                <p className="mt-3">A current list of our data Subprocessors is available at <a href="https://exitiumlabs.com/legal/subprocessors" className="text-blue-600 hover:text-blue-700">https://exitiumlabs.com/legal/subprocessors</a>. We do not sell your personal information.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Rights and Choices</h2>
              <div className="text-slate-600 leading-relaxed">
                <p className="mb-3">You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-slate-900">Opt-Out:</strong> Reply STOP to any message to immediately unsubscribe</li>
                  <li><strong className="text-slate-900">Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong className="text-slate-900">Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong className="text-slate-900">Correction:</strong> Request correction of inaccurate information</li>
                </ul>
                <p className="mt-3">To exercise these rights, contact us at: support@exitiumlabs.com. Where we act as a processor for a Business, we may direct your request to that Business.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. California Privacy Rights (CCPA/CPRA)</h2>
              <div className="text-slate-600 leading-relaxed space-y-3">
                <p>If you are a California resident, you have the rights to know, access, correct, and delete your personal information, and to not be discriminated against for exercising these rights.</p>
                <p><strong className="text-slate-900">Do Not Sell or Share:</strong> We do not sell or share (for cross-context behavioral advertising) your personal information. You may still submit a request regarding selling/sharing by contacting support@exitiumlabs.com.</p>
                <p>You may designate an authorized agent to make a request on your behalf. We will verify requests before responding.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Data Retention</h2>
              <p className="text-slate-600 leading-relaxed">
                We retain your information for as long as necessary to provide our services and comply with legal obligations. Call recordings and transcripts are retained for 90 days unless a longer period is required. When you opt out (STOP), we retain your phone number only to ensure we don't contact you again. You may request complete deletion by contacting us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Data Security</h2>
              <p className="text-slate-600 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the internet or electronic storage is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed">
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect information from children.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. SMS Messaging Terms</h2>
              <div className="text-slate-600 leading-relaxed space-y-3">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Message frequency varies based on the Business you opted in with</li>
                  <li>Message and data rates may apply</li>
                  <li>Reply STOP to opt-out at any time</li>
                  <li>Reply HELP for assistance or contact support@exitiumlabs.com</li>
                  <li>Messages will not be sent before 8:00 AM or after 9:00 PM in your local time zone</li>
                  <li>Carriers are not liable for delayed or undelivered messages</li>
                  <li>Your consent to receive messages is not a condition of any purchase</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact Us</h2>
              <div className="text-slate-600 leading-relaxed">
                <p className="mb-2">If you have questions about this Privacy Policy, please contact us:</p>
                <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <p><strong className="text-slate-900">EXITIUM Labs</strong></p>
                  <p>Email: support@exitiumlabs.com</p>
                  <p>Website: https://exitiumlabs.com</p>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-400 text-sm">
            © 2026 <span className="text-blue-600 font-semibold">EXITIUM Labs</span>. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;